import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Modal, Button } from '@gsa/afp-component-library';
import UpdateContractLineModalForm from './update-contract-line-modal-form';
import './update-contract-line.scss';
import ToastMessage from '../../../components/Toast/toast';

const UpdateLineItemsModal = ({
  onUpdate,
  closeModal,
  disabled = false,
  headerData,
  lineItems,
}) => {
  const [toast, setToast] = useState(null);
  const [checked, setChecked] = useState({});
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const setCheck = (field, value) => {
    setChecked((prev) => ({ ...prev, [field]: value }));
    if (!value) {
      setValue(field, '');
      setError(field, '');
    }
  };
  const setValue = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (value) setError(field, '');
  };
  const setError = (field, value) =>
    setErrors((prev) => ({ ...prev, [field]: value }));
  const formData = { checked, errors, values, setCheck, setValue, setError };

  const { awardedDate, contractEndDate, contractTerminationDate } = headerData;

  const notEmpty = (obj) => Object.values(obj).some((v) => !!v);
  const validateDate = (date) => {
    if (!moment(date, 'MM/DD/YYYY', true).isValid())
      throw new Error('Date must be in MM/DD/YYYY format');
  };
  const openDateParser = (date) => {
    validateDate(date);
    if (awardedDate && moment(date).isSameOrBefore(moment(awardedDate)))
      throw new Error(
        'The open date/time cannot be prior to the contract award date.',
      );
    return moment(date).format('YYYY-MM-DD');
  };
  const closeDateParser = (date) => {
    validateDate(date);
    if (
      contractTerminationDate &&
      moment(contractTerminationDate).isBefore(moment(date))
    )
      throw new Error(
        'The closeout date cannot be past the contract termination date',
      );
    if (contractEndDate && moment(contractEndDate).isBefore(moment(date)))
      throw new Error('The closeout date cannot be past the contract end date');
    return moment(date).format('YYYY-MM-DD');
  };
  const timeParser = (time) => `${time}:00`;
  const directParser = (val) => (val === 'Yes' ? 1 : 0);

  const fields = [
    { field: 'openDate', title: 'Open date', parser: openDateParser },
    { field: 'openTime', title: 'Open time', parser: timeParser },
    { field: 'closeDate', title: 'Closeout date', parser: closeDateParser },
    { field: 'closeTime', title: 'Closeout time', parser: timeParser },
    { field: 'isDirect', title: 'Direct', parser: directParser },
  ];

  const onSubmit = async () => {
    setErrors({});
    setToast(null);

    if (!notEmpty(checked)) {
      setToast({
        type: 'error',
        message: `Please select at least one field to update`,
      });
      return;
    }

    const updates = {};
    const errs = {};
    fields.forEach(({ field, title, parser }) => {
      if (!checked[field]) return;
      if (!values[field]) {
        errs[field] = `${title} is required`;
      } else {
        try {
          const val = parser(values[field]);
          updates[field] = val;
        } catch (err) {
          errs[field] = err.message;
        }
      }
    });
    if (notEmpty(errs)) {
      setErrors(errs);
      setToast({
        type: 'error',
        message: `Please enter a valid input for each selected field`,
      });
      return;
    }

    closeModal();
    onUpdate(lineItems, updates, values);
  };

  return (
    <Modal
      className="solicitation-modal"
      id="contract-header-maximum-value-modal"
      variant="large"
      onClose={closeModal}
      actions={
        <>
          <Button
            type="button"
            variant="unstyled"
            onClick={closeModal}
            label="Cancel"
            disabled={disabled}
          />
          <Button
            data-testid="modal-save-changes-button"
            type="button"
            onClick={onSubmit}
            label="Apply changes and close"
            disabled={disabled}
          />
        </>
      }
    >
      <div className="modal-content">
        <h2>Update selected lines</h2>
        {toast && (
          <ToastMessage
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
            closable
          />
        )}
        <p>{lineItems.length} Contract lines selected</p>
        <UpdateContractLineModalForm formData={formData} />
      </div>
    </Modal>
  );
};

UpdateLineItemsModal.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      scheduleLine: PropTypes.string,
      standardItem: PropTypes.shape({
        vehicleGroup: PropTypes.shape({
          title: PropTypes.string,
        }),
        vehicleType: PropTypes.string,
        standardItemNumber: PropTypes.string,
        title: PropTypes.string,
      }),
      modelYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      orderStartDate: PropTypes.string,
      orderEndDate: PropTypes.string,
    }),
  ).isRequired,
  onUpdate: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  headerData: PropTypes.shape({
    awardedDate: PropTypes.string,
    contractEndDate: PropTypes.string,
    contractTerminationDate: PropTypes.string,
  }).isRequired,
};

export default UpdateLineItemsModal;
